/**
 * Every entitlement our app supports, keyed to its Chargebee API `feature_id`. To gate a route/menu item on one,
 * add an entry here and set it as the `chargebeeFeatureId` on that item's RoutePermission.
 */
export const ChargebeeFeatureId = {
   clientVisibility: 'client-visibility',
   gapAcademyAccess: 'gap_academy_access',
   proposals: 'proposals',
   valueGapCalculator: 'value_gap_calculator',
} as const

export type ChargebeeFeatureId = (typeof ChargebeeFeatureId)[keyof typeof ChargebeeFeatureId]

export const chargebeeFeatureIds = Object.values(ChargebeeFeatureId)

/** Drops Chargebee features we don't model; used to filter live entitlement data. */
export const isChargebeeFeatureId = (featureId: string): featureId is ChargebeeFeatureId =>
   (chargebeeFeatureIds as readonly string[]).includes(featureId)

/**
 * Levels of the `gap_academy_access` entitlement (a Chargebee `custom` feature). Chargebee returns the raw
 * values capitalised (`"Full"` / `"Limited"`); we normalise to lowercase — see ChargebeeService.getEntitlementSnapshot.
 * `full` → Circle advisor group, `limited` → Circle trial group.
 */
export type GapAcademyAccessLevel = 'full' | 'limited'

/** Features that belong to the whole org, never to a seat: they always come from the customer-level read,
 *  even on a team plan where everything else is resolved per seat type. */
export const orgWideChargebeeFeatureIds: readonly ChargebeeFeatureId[] = [ChargebeeFeatureId.clientVisibility]

/**
 * The entitlements ONE user gets. Team plans mix core and grow seats on a single subscription, so their
 * features come from the user's seat add-on (`seatEntitlements`) plus the whole-org features; individual
 * plans have one seat type per subscription, so the customer-level list is already per-user.
 */
export const resolveUserEntitlements = (params: { orgEntitlements: ChargebeeFeatureId[]; seatEntitlements?: ChargebeeFeatureId[] }): ChargebeeFeatureId[] => {
   const { orgEntitlements, seatEntitlements } = params
   if (!seatEntitlements) {
      return orgEntitlements
   }
   return [...new Set([...orgEntitlements.filter(featureId => orgWideChargebeeFeatureIds.includes(featureId)), ...seatEntitlements])]
}

/** Chargebee subscription statuses, as returned on `subscription.status`. */
/** Core + Grow seats of a persisted grant; a 0 total means unusable seat metadata and reads as unknown. */
export const usableSeatTotal = (grant?: { coreUserCount?: number; growUserCount?: number }): number | undefined => {
   if (!grant) return undefined
   const seatCount = (grant.coreUserCount ?? 0) + (grant.growUserCount ?? 0)
   return seatCount > 0 ? seatCount : undefined
}

export const chargebeeSubscriptionStatuses = ['future', 'in_trial', 'active', 'non_renewing', 'paused', 'cancelled', 'transferred'] as const

export type ChargebeeSubscriptionStatus = (typeof chargebeeSubscriptionStatuses)[number]
